import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useAdminCompanyOptions } from '@/hooks/useAdminCompanyOptions';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { EnableCompaniesDialog } from '../../-components/EnableCompaniesDialog';
import { FeatureCompaniesTable } from '../../-components/FeatureCompaniesTable';
import { adminPlatformFeatureQueryOptions } from '../../-hooks/useAdminPlatformFeature';
import { featureCompaniesQueryOptions } from '../../-hooks/useFeatureCompanies';
import { useFeatureDetailPage } from '../../-hooks/useFeatureDetailPage';

const featureCompaniesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute(
  '/management/platform-features/(pages)/$featureId/',
)({
  validateSearch: featureCompaniesSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  loader: async ({ params, deps }) => {
    // Sequential, not `Promise.all`: the header is what the page renders first,
    // and the list is the larger of the two requests.
    //
    // Both rejections are swallowed for the same reason as on the list route: a
    // prefetch that throws hands the route to the router's error boundary, and
    // the page's own inline error banner never gets to render. The component's
    // `useQuery` calls hit the same failures and report them in place.
    await queryClient
      .ensureQueryData(adminPlatformFeatureQueryOptions(params.featureId))
      .catch(() => undefined);
    await queryClient
      .ensureQueryData(
        featureCompaniesQueryOptions({ featureId: params.featureId, ...deps }),
      )
      .catch(() => undefined);
  },
  component: FeatureDetailPage,
});

function FeatureDetailPage() {
  const { featureId } = Route.useParams();
  const page = useFeatureDetailPage(featureId);
  const {
    countryFilterOptions,
    facilityProcessFilterOptions,
    resolveFacilityProcess,
  } = useAdminCompanyOptions();

  return (
    <PageContainer>
      <FeatureCompaniesTable
        page={page}
        countryFilterOptions={countryFilterOptions}
        facilityProcessFilterOptions={facilityProcessFilterOptions}
        resolveFacilityProcess={resolveFacilityProcess}
      />

      <EnableCompaniesDialog page={page} />
    </PageContainer>
  );
}
